import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, MapPin, Phone, Store } from 'lucide-react';
import { getMerchantByCode } from '../lib/salinguntungApi';

const MerchantDetailPage = () => {
  const { code } = useParams();
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getMerchantByCode(code)
      .then((data) => {
        if (!data) {
          setNotFound(true);
        } else {
          setMerchant(data);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-16 md:pt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/merchants" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#2D9CDB] mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Kembali ke daftar merchant
          </Link>

          {loading ? (
            <div className="space-y-6">
              <Skeleton className="h-64 md:h-96 rounded-2xl w-full" />
              <Skeleton className="h-8 w-1/2" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : notFound ? (
            <div className="text-center py-24">
              <Store className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Merchant tidak ditemukan</h1>
              <p className="text-slate-500">Merchant yang Anda cari mungkin sudah tidak tersedia.</p>
            </div>
          ) : (
            <div>
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-8 bg-slate-100">
                {merchant.banner ? (
                  <img
                    src={merchant.banner}
                    alt={merchant.name}
                    className="w-full h-64 md:h-96 object-cover"
                  />
                ) : (
                  <div className="w-full h-64 md:h-96 flex items-center justify-center">
                    <Store className="h-16 w-16 text-slate-300" />
                  </div>
                )}
              </div>

              <div className="flex items-start gap-4 mb-6">
                {merchant.logo && (
                  <img
                    src={merchant.logo}
                    alt={`Logo ${merchant.name}`}
                    className="w-16 h-16 rounded-xl object-cover shadow-md flex-shrink-0"
                  />
                )}
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{merchant.name}</h1>
                  {merchant.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {merchant.categories.map((c) => (
                        <Badge key={c.id} className="bg-slate-900/10 text-slate-900 border-slate-900/20">
                          {c.category?.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-6">
                  {merchant.description && (
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 mb-2">Tentang Merchant</h2>
                      <p className="text-slate-600 whitespace-pre-line">{merchant.description}</p>
                    </div>
                  )}

                  {merchant.store_images?.length > 0 && (
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 mb-3">Galeri</h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {merchant.store_images.map((img, i) => (
                          <img
                            key={i}
                            src={img}
                            alt={`${merchant.name} ${i + 1}`}
                            className="w-full h-32 object-cover rounded-xl shadow"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 rounded-2xl p-6 h-fit space-y-4">
                  <h2 className="text-lg font-bold text-slate-900">Informasi Kontak</h2>
                  {merchant.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm">{merchant.address}</span>
                    </div>
                  )}
                  {merchant.phone_number && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-slate-500 flex-shrink-0" />
                      <a href={`tel:${merchant.phone_number}`} className="text-slate-700 text-sm hover:text-[#2D9CDB]">
                        {merchant.phone_number}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MerchantDetailPage;
